import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SimplePeer from 'simple-peer';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useNotification } from '../../context/NotificationContext';
import api, { consultationAPI, earningsAPI } from '../../utils/api';
import { formatImageUrl } from '../../utils/imageUtils';
import RatingModal from '../../components/RatingModal';
import IncomingRing from '../../assets/sounds/Crystalline_Dawn.mp3';

const SessionRoom = () => {
    const { consultationId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { socket } = useSocket();
    const { showSuccess, showError } = useNotification();
    const [showRatingModal, setShowRatingModal] = useState(false);

    // WebRTC State
    const [stream, setStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [callAccepted, setCallAccepted] = useState(false);
    const callAcceptedRef = useRef(callAccepted);
    callAcceptedRef.current = callAccepted;
    const [callEnded, setCallEnded] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [remoteSocketId, setRemoteSocketId] = useState(null);
    const [consultation, setConsultation] = useState(null);
    const [showEndCallConfirm, setShowEndCallConfirm] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting, connected, disconnected, failed
    const [reconnectCount, setReconnectCount] = useState(0);

    // Refs
    const myVideo = useRef();
    const userVideo = useRef();
    const connectionRef = useRef();
    const streamRef = useRef(); // Keep track of current stream for cleanup
    const audioRef = useRef(); // Audio for ringback

    useEffect(() => {
        // Initialize ringback tone
        audioRef.current = new Audio(IncomingRing);
        audioRef.current.loop = true;

        // Native Immersive Mode
        const enterImmersive = async () => {
            if (window.Capacitor) {
                const { StatusBar } = await import('@capacitor/status-bar');
                // NavigationBar plugin issue - skipping for now
                try {
                    await StatusBar.hide();
                    await StatusBar.setOverlaysWebView({ overlay: true });
                } catch (e) {
                    console.log('Native immersive mode error:', e);
                }
            }
        };

        const exitImmersive = async () => {
            if (window.Capacitor) {
                const { StatusBar } = await import('@capacitor/status-bar');
                try {
                    await StatusBar.show();
                    await StatusBar.setOverlaysWebView({ overlay: false });
                } catch (e) {
                    console.log('Native immersive mode exit error:', e);
                }
            }
        };

        enterImmersive();

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            exitImmersive();
        };
    }, []);

    // Main Consultation Logic
    useEffect(() => {
        if (!socket || !user) return;

        // 1. Get User Media
        const fetchMedia = (isVoiceOnly = false) => {
            const constraints = {
                video: isVoiceOnly ? false : {
                    facingMode: "user",
                },
                audio: true
            };

            navigator.mediaDevices.getUserMedia(constraints)
                .then((currentStream) => {
                    setStream(currentStream);
                    streamRef.current = currentStream;
                    socket.emit('join_video_room', consultationId);
                })
                .catch((err) => {
                    console.error("Failed to get media", err);
                    if (!isVoiceOnly) {
                        // Fallback to basic video if high res/facingMode fails
                        navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                            .then((fallbackStream) => {
                                setStream(fallbackStream);
                                streamRef.current = fallbackStream;
                                socket.emit('join_video_room', consultationId);
                            })
                            .catch(e => showError("Camera/Microphone access denied"));
                    } else {
                        showError("Microphone access denied");
                    }
                });
        };

        // Fetch Consultation Details
        const fetchConsultationDetails = async () => {
            try {
                const response = await api.get(`/consultation/${consultationId}`);
                const data = response.data;
                if (data.success) {
                    setConsultation(data.data);
                    const isVoiceOnly = data.data.consultation_type === 'audio';
                    fetchMedia(isVoiceOnly);

                    // Trigger earnings initiation for doctors
                    if (user?.role === 'doctor') {
                        try {
                            await earningsAPI.startCall({
                                consultationId,
                                consultationType: data.data.consultation_type || 'video'
                            });
                            console.log('💰 Earnings tracking initiated');
                        } catch (e) {
                            console.error('Failed to initiate earnings tracking:', e);
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch consultation", error);
                // Default to video if fetch fails for safety, but try fetchMedia at least
                fetchMedia(false);
            }
        };
        fetchConsultationDetails();

        // 3. Socket Listeners

        socket.on('user_connected', (id) => {
            console.log("Remote user connected:", id);
            setRemoteSocketId(id);
            stopRingback(); // Stop ringing when user connects
            setCallAccepted(true); // Requirement: If they connect, we consider it a "join" for redirection logic
            if (user?.role === 'doctor') {
                callUser(id);
            }
        });

        socket.on('receive_offer', (data) => {
            console.log("Received Offer from:", data.from);
            setRemoteSocketId(data.from);
            stopRingback(); // Stop ringing if we receive an offer (they are here)
            answerCall(data);
        });

        socket.on('receive_answer', (data) => {
            console.log("Received Answer");
            stopRingback();
            if (connectionRef.current) {
                connectionRef.current.signal(data.answer);
            }
        });

        socket.on('receive_ice_candidate', (data) => {
            if (connectionRef.current) {
                connectionRef.current.signal(data.candidate);
            }
        });

        socket.on('call_ended_remote', async () => {
            setCallEnded(true);
            setConnectionStatus('disconnected');
            stopRingback();
            if (consultationId) {
                try {
                    await earningsAPI.endCall({ consultationId });
                } catch (err) {
                    console.error('Failed to record earnings:', err);
                }
                try {
                    await consultationAPI.complete(consultationId);
                } catch (err) {
                    console.error('Failed to complete consultation:', err);
                }
            }
            handleCallEndCleanup();
            showError("Remote user ended the session");
        });

        return () => {
            socket.off('user_connected');
            socket.off('receive_offer');
            socket.off('receive_answer');
            socket.off('receive_ice_candidate');
            socket.off('call_ended_remote');
            stopRingback();
            // Stop Stream
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (connectionRef.current) {
                connectionRef.current.destroy();
            }
        };
        // eslint-disable-next-line
    }, [socket, consultationId, user]);

    // NEW: Attach stream to video element when it renders
    useEffect(() => {
        if (stream && myVideo.current) {
            myVideo.current.srcObject = stream;
        }
    }, [stream]);

    // NEW: Attach remote stream to user video when it renders
    useEffect(() => {
        if (remoteStream && userVideo.current) {
            userVideo.current.srcObject = remoteStream;
        }
    }, [remoteStream, callAccepted]);

    const stopRingback = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    };

    // --- WebRTC Functions ---

    const callUser = (id) => {
        console.log("Initiating Call to:", id);
        const peer = new SimplePeer({
            initiator: true,
            trickle: true,
            stream: streamRef.current,
            config: {
                iceServers: [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    { urls: 'stun:stun2.l.google.com:19302' },
                    { urls: 'stun:stun3.l.google.com:19302' },
                    { urls: 'stun:stun4.l.google.com:19302' },
                ],
                iceTransportPolicy: 'all'
            }
        });

        peer.on('signal', (data) => {
            socket.emit('make_offer', {
                offer: data,
                to: id
            });
        });

        peer.on('stream', (remoteStream) => {
            if (userVideo.current) {
                userVideo.current.srcObject = remoteStream;
            }
            setRemoteStream(remoteStream);
            setCallAccepted(true);
            stopRingback();
        });

        peer.on('connect', () => {
            console.log("Peer Connected!");
            setConnectionStatus('connected');
            stopRingback();
        });

        peer.on('error', (err) => {
            console.error("Peer Error:", err);
            setConnectionStatus('failed');
            if (reconnectCount < 3 && !callEnded) {
                console.log(`Attempting reconnect ${reconnectCount + 1}...`);
                setReconnectCount(prev => prev + 1);
                setTimeout(() => callUser(id), 2000);
            }
        });

        peer.on('close', () => {
            console.log("Peer Connection Closed");
            if (!callEnded && reconnectCount < 3) {
                console.log(`Attempting reconnect ${reconnectCount + 1} on close...`);
                setConnectionStatus('failed');
                setReconnectCount(prev => prev + 1);
                setTimeout(() => callUser(id), 2000);
            } else if (!callEnded) {
                setConnectionStatus('disconnected');
            }
        });

        // Removed duplicate 'receive_answer' listener here. It is handled globally.

        connectionRef.current = peer;
    };

    const answerCall = (data) => {
        setCallAccepted(true);
        stopRingback();
        const peer = new SimplePeer({
            initiator: false,
            trickle: true,
            stream: streamRef.current,
            config: {
                iceServers: [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' },
                    { urls: 'stun:stun2.l.google.com:19302' },
                    { urls: 'stun:stun3.l.google.com:19302' },
                    { urls: 'stun:stun4.l.google.com:19302' },
                ],
                iceTransportPolicy: 'all'
            }
        });

        peer.on('signal', (signalData) => {
            socket.emit('make_answer', {
                answer: signalData,
                to: data.from
            });
        });

        peer.on('stream', (remoteStream) => {
            if (userVideo.current) {
                userVideo.current.srcObject = remoteStream;
            }
            setRemoteStream(remoteStream);
        });

        peer.signal(data.offer);
        connectionRef.current = peer;
    };



    const handleCallEndCleanup = () => {
        if (connectionRef.current) {
            connectionRef.current.destroy();
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }

        // Redirect Logic
        console.log("👋 Call ended, navigating away. Role:", user?.role);
        if (user?.role === 'doctor') {
            if (callAcceptedRef.current) {
                navigate(`/prescription/create/${consultationId}`, { replace: true });
            } else {
                navigate(`/doctor/home`, { replace: true });
            }
        } else {
            // Patient needs to rate logic
            setShowRatingModal(true);
        }
    };

    const leaveCall = async () => {
        setCallEnded(true);
        stopRingback();

        if (!callAcceptedRef.current && consultationId) {
            try {
                await consultationAPI.reject(consultationId, { reason: 'rejected' });
            } catch (err) {
                console.error('Failed to notify backend of rejection:', err);
            }
        } else if (callAcceptedRef.current && consultationId) {
            try {
                await earningsAPI.endCall({ consultationId });
            } catch (err) {
                console.error('Failed to record earnings:', err);
            }
            try {
                await consultationAPI.complete(consultationId);
            } catch (err) {
                console.error('Failed to complete consultation:', err);
            }
        }

        socket.emit('end_call', { consultationId });
        setShowEndCallConfirm(false);
        handleCallEndCleanup();
    };

    const toggleMute = () => {
        const tracks = streamRef.current?.getAudioTracks?.();
        if (tracks?.[0]) {
            tracks[0].enabled = !tracks[0].enabled;
            setIsMuted(!isMuted);
        }
    };

    const toggleVideo = () => {
        const tracks = streamRef.current?.getVideoTracks?.();
        if (tracks && tracks.length > 0) {
            tracks[0].enabled = !tracks[0].enabled;
            setIsVideoOff(!isVideoOff);
        } else {
            showError("No video track available in this audio call");
        }
    };

    const ringPatient = () => {
        if (!socket || !consultation || !user) return;

        // Play Ringback Tone
        if (audioRef.current) {
            audioRef.current.play().catch(e => {
                if (e.name !== 'AbortError') {
                    console.error("Audio play failed", e);
                }
            });
        }

        // Emit 'call_user' event to backend
        console.log("🔔 Ringing Patient...", {
            socketId: socket.id,
            patient: consultation.patient_id,
        });

        socket.emit('call_user', {
            calleId: consultation.patient_id._id || consultation.patient_id,
            callerId: user._id || user.id,
            callerName: user.name,
            consultationId: consultationId,
            type: consultation.consultation_type || 'video'
        });
        showSuccess(`Ringing ${consultation.patient_id.name || 'User'}...`);
    };

    const ringDoctor = () => {
        if (!socket || !consultation || !user) return;

        // Play Ringback Tone
        if (audioRef.current) {
            audioRef.current.play().catch(e => {
                if (e.name !== 'AbortError') {
                    console.error("Audio play failed", e);
                }
            });
        }

        console.log("🔔 Ringing Doctor...", {
            socketId: socket.id,
            doctor: consultation.doctor_id,
        });

        socket.emit('call_user', {
            calleId: consultation.doctor_id._id || consultation.doctor_id,
            callerId: user._id || user.id,
            callerName: user.name,
            consultationId: consultationId,
            type: consultation.consultation_type || 'video'
        });
        showSuccess(`Ringing ${consultation.doctor_id?.name || 'Specialist'}...`);
    };

    // --- Render ---

    return (
        <div className="flex flex-col h-screen bg-gray-900 overflow-hidden relative">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/70 to-transparent flex justify-between items-center">
                <h2 className="text-white font-bold text-lg">Session #{consultationId.slice(-4)}</h2>
                <div className="bg-red-600/80 px-3 py-1 rounded text-white text-xs font-bold animate-pulse">
                    REC
                </div>
            </div>

            {/* Video Area */}
            <div className="flex-1 relative bg-black flex justify-center items-center">
                {/* My Video (Small Overlay) */}
                {stream && (
                    <div className="absolute top-4 right-4 w-32 h-48 bg-gray-800 rounded-xl overflow-hidden border-2 border-gray-600 shadow-xl z-20">
                        <video
                            playsInline
                            muted
                            ref={myVideo}
                            autoPlay
                            className="w-full h-full object-cover transform scale-x-[-1]"
                        />
                        <div className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-white bg-black/50">
                            {user?.name}
                        </div>
                    </div>
                )}

                {/* Remote Video (Main) */}
                {callAccepted && !callEnded ? (
                    <div className="w-full h-full relative">
                        <video
                            playsInline
                            ref={userVideo}
                            autoPlay
                            className="w-full h-full object-cover"
                        />
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                        <div className="relative">
                            {consultation && (
                                <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-gold shadow-2xl relative z-10">
                                    <img
                                        src={(() => {
                                            const profile = user?.role === 'doctor' ? consultation.patient_id?.profile_image : consultation.doctor_id?.profile_image;
                                            const name = user?.role === 'doctor' ? consultation.patient_id?.name : consultation.doctor_id?.name;
                                            return profile ? formatImageUrl(profile) : `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&size=150&background=random`;
                                        })()}
                                        alt="Remote User"
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.onerror = null; const name = user?.role === 'doctor' ? (consultation.patient_id?.name || 'User') : (consultation.doctor_id?.name || 'Provider'); e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=150&background=random`; }}
                                    />
                                </div>
                            )}
                            {/* Animated Pulse Ring */}
                            <div className="absolute inset-0 bg-gold/30 rounded-full animate-ping z-0"></div>
                        </div>
                        <h3 className="text-2xl font-bold text-white mt-8 mb-2">
                            {user?.role === 'doctor' ? `Calling ${consultation?.patient_id?.name || 'User'}...` : `Waiting for Specialist ${consultation?.doctor_name || ''}...`}
                        </h3>
                        <p className="text-gray-400 max-w-xs">
                            {connectionStatus === 'failed' ? "Connection unstable. Trying to reconnect..." : "Setting up secure connection. Stay on this screen."}
                        </p>

                        {connectionStatus === 'connecting' && (
                            <div className="mt-6 flex space-x-1">
                                <div className="w-2 h-2 bg-gold rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className="bg-gray-900 p-6 pb-12 flex justify-center items-center space-x-6">
                <button
                    onClick={toggleMute}
                    className={`p-4 rounded-full ${isMuted ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'} transition-colors`}
                >
                    {isMuted ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2"></line></svg>
                    ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path></svg>
                    )}
                </button>

                <button
                    onClick={() => setShowEndCallConfirm(true)}
                    className="p-5 rounded-full bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-600/30 transform hover:scale-105 transition-all"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L8.228 8.028A1 1 0 007.28 7.344H6.344a1 1 0 00-1.093.894"></path></svg>
                </button>

                <button
                    onClick={toggleVideo}
                    className={`p-4 rounded-full ${isVideoOff ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'} transition-colors`}
                >
                    {isVideoOff ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2"></line></svg>
                    ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                    )}
                </button>
            </div>

            {/* Ring Patient Button (Only for Doctor) - Floating Action */}
            {user?.role === 'doctor' && !callAccepted && (
                <button
                    onClick={ringPatient}
                    className="absolute bottom-32 right-6 bg-gold text-dark font-bold py-3 px-6 rounded-full shadow-lg hover:bg-yellow-500"
                >
                    Ring User
                </button>
            )}

            {/* Ring Doctor Button (Only for Patient) - Floating Action */}
            {user?.role === 'patient' && !callAccepted && (
                <button
                    onClick={ringDoctor}
                    className="absolute bottom-32 right-6 bg-gold text-dark font-bold py-3 px-6 rounded-full shadow-lg hover:bg-yellow-500"
                >
                    Ring Specialist
                </button>
            )}

            {/* End Call Confirmation Modal */}
            {showEndCallConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                    <div className="bg-gray-800 rounded-xl p-6 w-80 text-center shadow-2xl">
                        <h3 className="text-xl font-bold text-white mb-2">End Call?</h3>
                        <p className="text-gray-400 mb-6">Are you sure you want to hang up?</p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowEndCallConfirm(false)}
                                className="flex-1 bg-gray-700 text-white py-2 rounded-lg font-medium hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={leaveCall}
                                className="flex-1 bg-red-600 text-white py-2 rounded-lg font-bold hover:bg-red-700"
                            >
                                End Call
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rating Modal for Patient */}
            {showRatingModal && (
                <RatingModal
                    consultationId={consultationId}
                    doctorId={consultation?.doctor_id?._id || consultation?.doctor_id}
                    onClose={() => navigate('/patient/home')}
                />
            )}
        </div>
    );
};

export default SessionRoom;
